import * as React from "react";
import * as ReactDOM from "react-dom";
import * as utils from "../utils/utils";
import * as utilsCookies from "../utils/cookies";
import { getGeneralSetting } from "../api/settings";
import { getAllEverSignedInUsers, signin } from "../api/accounts";
import { getUserInfo } from "../api/users";
import { Loader, LoaderStyle } from "../components/loader";
import { Button, ButtonSize, ButtonType } from "../components/button";
import { Alert, AlertType } from "../components/alert";
import { FormGroup } from "../components/form";
import { InputSideButton, InputMask, InputSize } from "../components/input";
import { TextArea } from "../components/textArea";
import { TextBox } from "../components/textBox";
import { IUser } from "../models/get";
import { CheckBox } from "../components/checkbox";
import { getHost } from "../utils/environment";
import { Popup, PopupAlign, PopupProvider, PopupTrigger } from "../components/popup";
import { ContextMenu, MenuItem, Separator } from "../components/contextmenu/index";

interface IProperties
{
    match: any,
    location: any,
    history: any,
    staticContext: any
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt die momentane Ansicht des Logins an.
     */
    mode?: "chooser" | "username" | "password",

    /**
     * Enthält Einstellungen für das Login Form.
     */
    settings?: {
        dataProtectionUrl?: string,
        termsOfUseUrl?: string,
        headerImage?: string,
        imprint?: string,
        newAccountText?: string,
        newAccountLink?: string
    },

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        username?: string,
        password?: string,
        isPersistent?: boolean
    },

    /**
     * Enthält abgespeckte Daten des Benutzer welche bei einem
     * Klick auf "Weiter" geladen wird.
     */
    user?: IUser,

    /**
     * Enthält alle Benutzer die an dem Rechner des Anwenders mindestens einmal
     * angemeldet waren
     */
    users?: IUser[],

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export default class LoginPage extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isNextButtonDisabled: boolean = true;
    private _isLoginButtonDisabled: boolean = true;
    private _selectedAccount: IUser;

    /**
     * Erstellt eine neue Instanz der Klasse @see Help.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: IProperties)
    {
        super(props);

        this.state = {
            isLoading: true,
            mode: "username",
            settings: {},
            form: {
                isPersistent: false
            },
            errors: {}
        }

        this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        };
    }

    async componentWillMount()
    {
        var headerImage: string = await getGeneralSetting<string>("general.login.headerImage");
        var newAccountText: string = await getGeneralSetting<string>("general.login.newAccountText");
        var newAccountLink: string = await getGeneralSetting<string>("general.login.newAccountLink");
        var dataProtectionUrl: string = await getGeneralSetting<string>("general.dataProtectionUrl");
        var termsOfUseUrl: string = await getGeneralSetting<string>("general.termsOfUseUrl");
        var imprint: string = await getGeneralSetting<string>("general.imprint");
        var users: IUser[] = await getAllEverSignedInUsers();

        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.settings = {
                dataProtectionUrl,
                termsOfUseUrl,
                headerImage,
                imprint,
                newAccountText,
                newAccountLink
            };

            state.users = users;
            if (users != null && users.length === 1)
            {
                state.mode = "password";
                state.form.username = users.first().email;
                state.user = users.first();
            }
            else if (users != null && users.length > 1)
            {
                state.mode = "chooser";
            }

            return state;
        });
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps, nextState: IState)
    {
        this._isNextButtonDisabled = true;
        this._isLoginButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            if (nextState.mode === "username" && form.username !== nextState.form.username)
                this._isNextButtonDisabled = false;

            if (nextState.mode === "password" && form.password !== nextState.form.password)
                this._isLoginButtonDisabled = false;
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0] === "" ? undefined : value[0];
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer CheckBox ändert.
     * @param e Das auslösende Ereignis.
     */
    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) =>
        {
            state.form.isPersistent = value;
            return state;
        });
    }

    /**
     * Wählt einen Benutzer aus der "Konto auswahl" aus.
     * @param account Das Konto welches ausgewählt werden soll.
     */
    private onAccountClick = (account: IUser) =>
    {
        this.setState({
            mode: "password",
            form: {
                username: account.email
            },
            user: account
        });
    }

    /**
     * Springt zur manuellen Benuter Eingabe.
     */
    private onOtherAccountClick = () =>
    {
        this.setState((state: IState) =>
        {
            state.mode = "username";
            state.form.username = undefined;
            state.form.password = undefined;
            return state;
        });
    }

    private onRemoveAccount = async () =>
    {
        if (this._selectedAccount == null)
            return;

        var accounts = utilsCookies.get<string>("ACCS", true);
        if (accounts)
        {
            let accsArray: string[] = accounts.split(",");

            // Account aus Cookie entfernen.
            accsArray.removeAt(i => i === this._selectedAccount.id.toString());
            utilsCookies.set("ACCS", accsArray.join(","), {
                expires: 30
            });

            // UI Aktualisieren.
            await this.componentWillMount();
        }
    }

    /**
     * Wird ausgeführt wenn auf den "Weiter" Button geklickt wird.
     */
    private onNextClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { form } = this.state;
            var user: IUser = await getUserInfo(form.username);

            if (user == null)
            {
                user = {
                    email: form.username
                };
            }

            this.setState({
                isLoading: false,
                mode: "password",
                user: user
            });
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Wird ausgeführt wenn auf den "Zurück" Button geklickt wird.
     */
    private onBackClick = () =>
    {
        this.setState((state: IState) =>
        {
            state.mode = (state.users != null && state.users.length > 1) ? "chooser" : "username";
            state.form.username = undefined;
            state.form.password = undefined;
            return state;
        });
    }

    /**
     * Wird ausgeführt wenn auf den "Weiter" Button geklickt wird.
     */
    private onSigninClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { form } = this.state;
            await signin({
                Email: form.username,
                Password: form.password,
                IsPersistent: form.isPersistent
            });

            var uri: uri.URI = new URI(this.props.location.pathname + this.props.location.search);
            var search = uri.search(true);

            if (search != null && search.ReturnUrl != null)
                location.href = getHost() + search.ReturnUrl;
            else
                location.href = getHost();
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                alertMessage = errors["LoginError"];
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Wird ausgeführt, sobald im Form eine Taste betätigt wird.
     * @param e Das auslösende Event.
     */
    private onKeyUp = (e: React.KeyboardEvent<HTMLElement>) =>
    {
        if (e.key === "Enter")
        {
            if (this.state.mode === "username" && !this._isNextButtonDisabled)
                this.onNextClick();
            else if (this.state.mode === "password" && !this._isLoginButtonDisabled)
                this.onSigninClick();
        }
        else if (e.key === "Escape")
        {
            if (this.state.mode === "password")
                this.onBackClick()
        }
    }

    /**
     * Rendert das Form für die Eingabe des Benutzernamens.
     */
    private renderChooser(): JSX.Element
    {
        const { settings, form, users, errors } = this.state;

        return (
            <div>
                <div className="login_form_info">Konto auswählen</div>
                <ul className="login_form_chooser">
                    {
                        users.map((user: IUser) =>
                        {
                            return (
                                <li
                                    key={user.id}
                                    className="login_form_chooser_item"
                                    onClick={() => this.onAccountClick(user)}>
                                    <img src={user.avatar} />
                                    <span>{user.email}</span>
                                    <PopupProvider
                                        id="popup"
                                        trigger={PopupTrigger.Click}
                                        triggerStopPropagation={true}
                                        dataSource={user}
                                        onOpening={(e, user) => this._selectedAccount = user}>
                                        <i className="login_form_chooser_item mdi mdi-dots-vertical"></i>
                                    </PopupProvider>
                                </li>
                            );
                        })
                    }
                    <li
                        key={999999}
                        className="login_form_chooser_item"
                        onClick={this.onOtherAccountClick}>
                        <i className="login_form_chooser_item-other mdi mdi-plus"></i>
                        <span>Anderes Konto verwenden</span>
                    </li>
                </ul>
            </div>
        );
    }

    /**
     * Rendert das Form für die Eingabe des Benutzernamens.
     */
    private renderUsernameForm(): JSX.Element
    {
        const { settings, form, users, errors } = this.state;

        return (
            <div>
                <div className="login_form_info">Anmelden</div>
                <FormGroup>
                    <TextBox
                        placeholder="E-Mail"
                        value={form.username}
                        size={InputSize.normal}
                        autoFocus={true}
                        errorMessage={errors["Username"]}
                        onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "username")} />
                </FormGroup>
                {
                    (settings.newAccountText && settings.newAccountLink) &&
                    <div className="login_form_new-account">
                        <a href={settings.newAccountLink} target="_target">{settings.newAccountText}</a>
                    </div>
                }
                <div className="login_form_action">
                    {
                        (users != null && users.length > 1) &&
                        <Button
                            style={{
                                width: 100,
                                marginRight: 10
                            }}
                            text="Zurück"
                            type={ButtonType.primary}
                            size={ButtonSize.normal}
                            onClick={this.onBackClick} />
                    }
                    <Button
                        style={{
                            width: 100
                        }}
                        text="Weiter"
                        type={ButtonType.primary}
                        size={ButtonSize.normal}
                        isDisabled={this._isNextButtonDisabled}
                        onClick={this.onNextClick} />
                </div>
            </div>
        );
    }

    /**
     * Rendert das Form für die Eingabe des Passworts.
     */
    private renderPasswordForm(): JSX.Element
    {
        const { settings, form, user, errors } = this.state;

        return (
            <div>
                <div className="login_form_user">
                    <img src={user.avatar} />
                    <span>{user.email}</span>
                </div>
                <div className="login_form_info">Kennwort eingeben</div>
                <FormGroup>
                    <TextBox
                        placeholder="Kennwort"
                        value={form.password}
                        isPassword
                        size={InputSize.normal}
                        autoFocus={true}
                        errorMessage={errors["Password"]}
                        onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "password")} />
                </FormGroup>
                <CheckBox
                    style={{
                        marginTop: 8
                    }}
                    label="Angemeldet bleiben"
                    isChecked={form.isPersistent}
                    onCheckedChanged={this.onCheckboxChanged} />
                <div className="login_form_action">
                    <Button
                        style={{
                            width: 100,
                            marginRight: 10
                        }}
                        text="Zurück"
                        type={ButtonType.primary}
                        size={ButtonSize.normal}
                        onClick={this.onBackClick} />
                    <Button
                        style={{
                            width: 100
                        }}
                        text="Anmelden"
                        type={ButtonType.primary}
                        size={ButtonSize.normal}
                        isDisabled={this._isLoginButtonDisabled}
                        onClick={this.onSigninClick} />
                </div>
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        var {
            isLoading,
            mode,
            settings,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        return (
            <div className="login-page">
                <div className="login-bg is-image"></div>
                <div className="login">
                    <div className="login_content">
                        <div className="login_product">
                            <img
                                className="login_product_logo"
                                src="/img/logos/terminplaner_logo_white.png" />
                            <span className="login_product_name">Terminplaner</span>
                        </div>
                        <div className="login_form" onKeyUp={this.onKeyUp}>
                            {isLoading && <Loader loaderStyle={LoaderStyle.style1} />}
                            <Alert
                                style={{
                                    marginBottom: 10
                                }}
                                alertType={alertType}
                                isVisible={alertMessage ? true : false}
                                isClosable={true}
                                onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                                {alertMessage}
                            </Alert>
                            {
                                settings.headerImage &&
                                <div className="login_form_header">
                                    <img src={settings.headerImage} />
                                </div>
                            }
                            {
                                !settings.headerImage &&
                                <div className="login_form_header is-iclogo">
                                    <span>in-com</span>
                                </div>
                            }
                            {mode === "chooser" && this.renderChooser()}
                            {mode === "username" && this.renderUsernameForm()}
                            {mode === "password" && this.renderPasswordForm()}
                        </div>
                    </div>
                </div>

                <footer className="login-footer">
                    <span className="login-footer_item">©{new Date().getFullYear()} in-com</span>
                    {
                        settings.termsOfUseUrl &&
                        <a
                            className="login-footer_item"
                            href={settings.termsOfUseUrl}
                            target="_target">Nutzungsbedingungen</a>
                    }
                    {
                        settings.dataProtectionUrl &&
                        <a
                            className="login-footer_item"
                            href={settings.dataProtectionUrl}
                            target="_target">Datenschutz & Cookies</a>
                    }
                    {
                        settings.imprint &&
                        <a
                            className="login-footer_item"
                            href={settings.imprint}
                            target="_target">Impressum</a>
                    }
                </footer>

                <Popup
                    id="popup"
                    align={PopupAlign.targetNodeBottomRight}>
                    <ContextMenu>
                        <MenuItem
                            icon="close"
                            header="Auswahl entfernen"
                            onClick={this.onRemoveAccount} />
                    </ContextMenu>
                </Popup>
            </div>
        );
    }
}